import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Tooltip, useToast } from '@vd/ui';
import { Check, Clapperboard, EyeOff, LayoutDashboard, LayoutGrid, Plus, Rocket } from 'lucide-react';
import { PageHeader, pageBackground } from '../features/shell/PageHeader';
import { useAuth, useCurrentCompany } from '../features/auth/AuthProvider';
import { api } from '../lib/api';
import { CreateProjectModal } from '../features/modals/CreateProjectModal';
import { McpSetupContent } from './McpSetupPage';
import {
  readSkillDownloaded,
  downloadSkill,
  SKILL_DOWNLOADED_EVENT,
  TOADO_CWS_URL,
} from '../features/onboarding/getStarted';
import styles from './HomePage.module.css';
import { widgetSetupProjectId } from '../lib/widgetSetupProject';
import { DashboardSections } from '../features/dashboard/DashboardSections';
import { WhatsNewCard } from '../features/dashboard/WhatsNewCard';
import { ToursList, TourLaunch } from '../features/tours/TourLaunch';
import { useTours } from '../features/tours/TourProvider';
import { useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';
import { WalkthroughVideo } from '../features/onboarding/WalkthroughVideo';

/**
 * The HomePage is the user's persistent landing surface — clicking the
 * frog logo always returns them here. It does NOT auto-redirect to a
 * project board (that's the sign-in flow's job, separately). Three render
 * states:
 *
 *   1. User has no company → "Continue setup" panel pointing back at
 *      /onboarding (signup never finished).
 *   2. Otherwise → the welcome panel with the get-going checklist. Always
 *      visible from the frog click, regardless of whether the user has
 *      tickets — the user explicitly asked that this page remain
 *      reachable as a "home / start here again" surface.
 */
export function HomePage() {
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "Dashboard"] });
  const { currentCompanyId } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [mcpOpen, setMcpOpen] = useState(false);
  // localStorage-backed flag. useState initializer reads once at mount;
  // subsequent updates persist via downloadSkill (which also sets state
  // here). Resets only on browser-data-clear, which is fine — re-
  // downloading is idempotent.
  const [skillDownloaded, setSkillDownloaded] = useState<boolean>(() => readSkillDownloaded());

  // Keep skillDownloaded in sync if the user triggers the download
  // from the sidebar instead of this page. Listens for both the
  // cross-tab `storage` event and the in-tab `vd:skill-downloaded`
  // event the helper dispatches (storage only fires across tabs).
  useEffect(() => {
    const refresh = () => setSkillDownloaded(readSkillDownloaded());
    window.addEventListener(SKILL_DOWNLOADED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(SKILL_DOWNLOADED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const noCompany = !currentCompanyId;
  // The workspace tour, when tours are on: first in Get going (Jason, 28 Oct).
  const tourList = useTours();
  const tourFirst = tourList.enabled && tourList.tours.some((tour) => tour.id === 'board') ? 'board' : null;

  // Drives the ✓ checkmark on each step. Refetches whenever the user
  // returns to / so a project they just created (or an extension they
  // just installed) flips its tile to "done" without a hard reload.
  const { data: homeState, isPending: homeStatePending } = useQuery({
    queryKey: ['home-state', currentCompanyId],
    // This workspace's own projects and tickets decide whether it is starting out (17 Oct).
    queryFn: () => api.getHomeState(currentCompanyId),
    enabled: !noCompany,
    refetchOnWindowFocus: true,
  });

  // Read existing project colors from the cache (the sidebar primes this)
  // so the create modal pre-selects the next-unused palette color.
  const { data: existingProjects = [] } = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => (currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([])),
    enabled: !noCompany,
  });
  // The widget routes are owner-only, so only an owner is offered the widget.
  const isOwner = useCurrentCompany()?.role === 'owner';
  /**
   * Set up the Bug Collection Widget (Jason, 24 Oct): the widget sends what it
   * collects into a project, so without one it says to create one first; with
   * one it opens that project's widget settings with the setup wizard started,
   * the project you last opened here, else this workspace's first.
   */
  const widgetProjectId = isOwner ? widgetSetupProjectId(homeState?.lastProjectId, existingProjects) : null;
  const setUpWidget = () => {
    if (!widgetProjectId) {
      toast.push({
        variant: 'info',
        title: 'Create a project first',
        description: 'The widget sends what your customers report into a project. Create one in the first step, then set up the widget.',
      });
      return;
    }
    navigate(`/projects/${widgetProjectId}/settings?tab=widget&setup=1`);
  };

  /**
   * The Getting started bar (TOADO-722): open until setup is done, unless the
   * person has opened or closed it, which is kept for them on the server so
   * it is the same in every browser. Shown open while the answer loads, so a
   * new person never meets an empty page.
   */
  const setupDone = !!homeState?.hasProject && (!!homeState.extensionInstalled || !!homeState.widgetInstalled)
    && !!homeState.mcpConfigured && skillDownloaded;
  const chosen = homeState?.welcomeClosed;
  /**
   * Until the workspace has its first ticket, Getting started cannot be hidden
   * (Jason, 24 Oct, t-x0pvPC): it is the way to that ticket. Hidden before, it
   * stays open anyway, and neither hide button is drawn.
   */
  const canHideWelcome = !!homeState?.hasTickets;
  const welcomeOpen = !canHideWelcome ? true : chosen === null || chosen === undefined ? !setupDone : !chosen;
  /**
   * Starting out (Jason, 16 Oct): until the workspace has a project and a
   * ticket in it, the page is the welcome panel alone, the full width: no
   * What's new and no Dashboard cards, which would have nothing to show. Also
   * while the answer loads, so a new person never sees empty cards flash by.
   */
  const starting = !homeState || !(homeState.hasProject && homeState.hasTickets);
  const setWelcome = async (open: boolean) => {
    // Shown at once; the server's answer settles it.
    qc.setQueryData(['home-state', currentCompanyId], (old: typeof homeState) => (old ? { ...old, welcomeClosed: !open } : old));
    try {
      await api.setWelcomeClosed(!open);
    } catch (e) {
      toast.push({ variant: 'danger', title: open ? 'Could not open Getting started' : 'Could not close Getting started', description: (e as Error).message });
    }
    await qc.invalidateQueries({ queryKey: ['home-state'] });
  };

  const handleCreate = async (data: { name: string; color: string; urlPattern?: string }) => {
    if (!currentCompanyId) return;
    try {
      const project = await api.createProject(currentCompanyId, {
        name: data.name,
        color: data.color,
        urlPattern: data.urlPattern,
      });
      await qc.invalidateQueries({ queryKey: ['projects', currentCompanyId] });
      toast.push({ variant: 'success', title: 'Project created', description: project.name });
      navigate(`/projects/${project.id}/board`);
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Could not create project', description: (e as Error).message });
    }
  };

  if (noCompany) {
    return (
      <div className={styles.shell}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <img src="/favicon.svg" alt="" className={styles.brandMark} />
            <h1 className={styles.title}>Welcome to Toado</h1>
          </div>
          <p className={styles.subtitle}>
            Looks like your setup isn't complete: you don't have a company yet.
            Finish onboarding to create one and your first project.
          </p>
          <Button variant="primary" onClick={() => navigate('/onboarding')}>
            Continue setup
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.dashShell} ${pageBackground}`}>
      {/* The shared page header (16 Oct), its four actions on the right (TOADO-859): the same
          four whatever the workspace holds. With no last board, New ticket and Open my last
          board go to the projects page. Get started opens and hides the welcome panel. */}
      <PageHeader icon={<LayoutDashboard size={18} />} title="Dashboard" testId="dash-header"
        actions={
        <div className={styles.titleActions} data-testid="dash-quick-actions">
          {/* Links, since each goes somewhere: they open in a new tab, and read as links. */}
          {/* The two that go to the last board wait for it to be known, so a quick click
              never lands on the projects page instead. */}
          <BoardLink pending={homeStatePending} testId="dash-new-ticket"
            to={homeState?.lastProjectId ? `/projects/${homeState.lastProjectId}/board?new=1` : '/projects'}>
            <Plus size={14} aria-hidden="true" />New ticket
          </BoardLink>
          <Link className={styles.action} data-testid="dash-start-walkthrough" to="/recordings">
            <Clapperboard size={14} aria-hidden="true" />Start a video recording
          </Link>
          <BoardLink pending={homeStatePending} testId="dash-last-board"
            to={homeState?.lastProjectId ? `/projects/${homeState.lastProjectId}/board` : '/projects'}>
            <LayoutGrid size={14} aria-hidden="true" />Open my last board
          </BoardLink>
          {canHideWelcome && (
            <Button variant="secondary" size="sm" leftIcon={<Rocket size={14} />} data-testid="dash-get-started"
              aria-expanded={welcomeOpen} onClick={() => void setWelcome(!welcomeOpen)}>
              {welcomeOpen ? 'Hide getting started' : 'Get started'}
            </Button>
          )}
        </div>
        } />
      <div className={styles.dashboard}>
      {/* The main column opens with What's new and the welcome panel; the panel
          on the right starts level with them, under the title row (16 Oct). */}
      <DashboardSections companyId={starting ? null : currentCompanyId ?? null} lead={<>
      {homeState && !starting && <WhatsNewCard dismissed={homeState.whatsNewDismissed} companyId={currentCompanyId} />}
      {welcomeOpen && (
      <div className={styles.card} data-testid="welcome-open">
        {canHideWelcome && (
          <button type="button" className={styles.welcomeHide} aria-label="Hide getting started" title="Hide getting started"
            onClick={() => void setWelcome(false)} data-testid="welcome-hide">
            <EyeOff size={16} aria-hidden="true" />
          </button>
        )}
        <div className={styles.brand}>
          <img src="/favicon.svg" alt="" className={styles.brandMark} />
          <h2 className={styles.title}>Welcome to Toado</h2>
        </div>
        <p className={styles.subtitle}>
          You're all set up. File your first ticket to start capturing bugs. Your screenshots,
          console output, and AI-agent comments will all land in your project board.
        </p>

        <div className={styles.contentRow}>
          <div className={styles.stepsCol}>
            <div className={styles.stepsLabel} data-tour-anchor="home.get-going">Get going</div>
            {/* The tour first (Jason, 28 Oct): with no project yet, it starts by
                making one with the Create project button below. */}
            {tourFirst && (
              <div className={styles.tourFirst} data-testid="dash-tour">
                <span className={styles.tourIcon} aria-hidden="true"><Rocket size={16} /></span>
                <div className={styles.stepBody}>
                  <span className={styles.stepTitle}>Start with the tour</span>
                  <span className={styles.stepDesc}>
                    {homeState?.hasProject
                      ? 'A few minutes on your board, a sample ticket, your project\'s settings and connecting Claude Code.'
                      : 'It starts by creating your first project, then walks you through your board, a sample ticket, the project\'s settings and connecting Claude Code.'}
                  </span>
                </div>
                <TourLaunch tourId={tourFirst} label="Workspace tour" primary />
              </div>
            )}
            <ol className={styles.steps}>
          <li className={styles.step}>
            <StepBadge index={1} done={!!homeState?.hasProject} />
            <div className={styles.stepBody}>
              <span className={styles.stepTitle}>Create your first project</span>
              <span className={styles.stepDesc}>
                Projects organize captures and tickets. You can have many; URL patterns auto-route incoming captures.
              </span>
            </div>
            {/* Secondary beside the tour, which starts here when there is no project (Jason, 28 Oct). */}
            <Button variant={tourFirst ? 'secondary' : 'primary'} onClick={() => setCreateOpen(true)} data-tour-anchor="home.create-project">
              {homeState?.hasProject ? 'Create another' : 'Create project'}
            </Button>
          </li>
          <li className={styles.step}>
            {/* Choose your capture path: EITHER the extension OR the
                embedded widget completes this step. The widget is the
                right answer for products you own — one line of code, no
                per-user install — so the onboarding no longer implies the
                extension is the only way to capture. */}
            <StepBadge index={2} done={!!homeState?.extensionInstalled || !!homeState?.widgetInstalled} />
            <div className={styles.stepBody}>
              <span className={styles.stepTitle}>Set up a way to capture bugs</span>
              <span className={styles.stepDesc}>
                Use the Chrome extension to capture any page you visit, with its screenshot, console,
                network requests and errors. Or add the Bug Collection Widget to a site you own with
                one line of code, so your customers can submit bugs with a form, by pointing at the
                problem, or by recording their screen. Either path completes this step.
              </span>
            </div>
            <div className={styles.stepActions}>
              {/* Straight to the Chrome Web Store listing, now that it carries 0.3.0 (Jason, 28 Oct). */}
              <Tooltip multiline content="The Chrome extension captures any page you visit into a ticket: a screenshot, the console, network requests and errors, ready for you or an AI agent to fix.">
                <a href={TOADO_CWS_URL} target="_blank" rel="noopener noreferrer" data-testid="dash-get-extension">
                  <Button variant="secondary">Get Extension</Button>
                </a>
              </Tooltip>
              {/* For every owner (t-AnYHk6): it was drawn only with a last project in the
                  workspace, so a workspace without one had no way to the widget from here. */}
              {isOwner && (
                <Tooltip multiline content={widgetProjectId
                  ? 'The Bug Collection Widget lets your customers submit bugs from your own site: with a form, by pointing at the problem, or by recording their screen.'
                  : 'The widget sends what your customers report into a project, so create a project first.'}>
                  <Button variant="secondary" data-testid="dash-setup-widget" onClick={setUpWidget}>
                    Set up the Bug Collection Widget
                  </Button>
                </Tooltip>
              )}
            </div>
          </li>
          <li className={styles.step}>
            <StepBadge index={3} done={!!homeState?.mcpConfigured} />
            <div className={styles.stepBody}>
              <span className={styles.stepTitle}>Connect via MCP</span>
              <span className={styles.stepDesc}>
                Hook up Claude Code, Cursor, or any MCP client so AI agents can read tickets and file new ones from your editor.
              </span>
            </div>
            {/* Opens the MCP setup flow in an in-page modal so the user
                stays on the dashboard. Was navigate('/mcp'). */}
            <Button variant="secondary" onClick={() => setMcpOpen(true)}>
              Connect Now
            </Button>
          </li>
          <li className={styles.step}>
            <StepBadge index={4} done={skillDownloaded} />
            <div className={styles.stepBody}>
              <span className={styles.stepTitle}>Download the Claude Code skill</span>
              <span className={styles.stepDesc}>
                Drop the pre-built triage skill into your Claude Code project so it can fix tickets autonomously from the kanban board.
              </span>
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                downloadSkill();
                setSkillDownloaded(true);
              }}
            >
              {skillDownloaded ? 'Download again' : 'Download skill'}
            </Button>
          </li>
        </ol>
            <ToursList except={tourFirst ?? undefined} />
          </div>

          {/* The walkthrough video: a still that opens it large, and which
              video it is set in Admin (TOADO-983). Layout is a sticky-top
              column on desktop, stacks below the steps on narrow viewports
              (see HomePage.module.css media query). */}
          <aside className={styles.videoCol}>
            <div className={styles.videoLabel}>Watch the walkthrough</div>
            <WalkthroughVideo />
          </aside>
        </div>
      </div>
      )}
      </>} />

        <CreateProjectModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreate}
          existingColors={existingProjects.map((p) => p.color)}
        />

        {/* In-page MCP setup. Reuses McpSetupContent (the body of the
            standalone /mcp page) so the dashboard + the full page show
            identical setup steps. */}
        <Modal
          open={mcpOpen}
          onClose={() => setMcpOpen(false)}
          width={780}
          labelledBy="home-mcp-modal-title"
        >
          <ModalHeader id="home-mcp-modal-title">Connect via MCP</ModalHeader>
          <ModalBody>
            <McpSetupContent />
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={() => setMcpOpen(false)}>Done</Button>
          </ModalFooter>
        </Modal>

      </div>
    </div>
  );
}

/**
 * A title-row link to the last board. Until the home state says which board
 * that is, it is not a link at all (its stand-in has no role and no target), so
 * it cannot send a quick click to the projects page.
 */
function BoardLink({ pending, to, testId, children }: { pending: boolean; to: string; testId: string; children: ReactNode }) {
  if (pending) {
    return <span className={`${styles.action} ${styles.actionPending}`} data-testid={testId} aria-disabled="true">{children}</span>;
  }
  return <Link className={styles.action} data-testid={testId} to={to}>{children}</Link>;
}

/**
 * Step indicator — number badge by default, swaps to a check icon (and a
 * "done" green tint) when the step has been completed. Same dimensions
 * either way so the step row layout doesn't shift when a flag flips.
 */
function StepBadge({ index, done }: { index: number; done: boolean }) {
  return (
    <span className={done ? `${styles.stepNum} ${styles.stepNumDone}` : styles.stepNum}>
      {done ? <Check size={14} strokeWidth={3} /> : index}
    </span>
  );
}
